import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import useApi from "../../hooks/useApi";

const BranchTransferDocListModal = ({
  isOpen,
  onClose,
  BASE_URL,
  COMPANY_CODE,
  DEPARTMENT,
  crAccount,
  onBranchTransferSelected, // callback(row, items) to parent
}) => {
  const { request } = useApi();

  const [docList, setDocList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingItemsFor, setLoadingItemsFor] = useState(null); // TROUT_NO being fetched

  // Fetch document list when modal opens
  useEffect(() => {
    if (!isOpen) {
      setDocList([]);
      return;
    }

    const account = crAccount || "-";
    const dept = DEPARTMENT || "-";

    setLoadingList(true);
    request({
      url: `${BASE_URL}/api/CRMDocListView/${COMPANY_CODE}/BTOUTINTERBRANCH/${account}/${dept}/-/-/-/${dept}/1/50`,
      method: "GET",
    })
      .then((res) => setDocList(Array.isArray(res) ? res : []))
      .catch(() => setDocList([]))
      .finally(() => setLoadingList(false));
  }, [isOpen, BASE_URL, COMPANY_CODE, DEPARTMENT, crAccount]);

  // Clicking a row: fetch items, send both to parent, close modal
  const handleRowClick = async (row) => {
    setLoadingItemsFor(row.TROUT_NO);
    try {
      const url = `${BASE_URL}/api/CRMDocTransfer/${COMPANY_CODE}/TRANSFER/BRANCHTRFRDETAIL/-/-/${DEPARTMENT}/${row.TROUT_NO}/-`;
      const res = await request({ url, method: "GET" });
      const items = Array.isArray(res) ? res : [];
      onBranchTransferSelected(row, items); // send to parent
      onClose();
    } catch {
      alert("Failed to fetch items for this transfer document.");
    } finally {
      setLoadingItemsFor(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="flex flex-col h-full p-2">
      <Typography variant="subtitle2" className="text-gray-500 mb-3">
        Click a row to import its items into the document
      </Typography>

      {loadingList ? (
        <div className="flex justify-center items-center py-10">
          <CircularProgress size={32} />
        </div>
      ) : docList.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-sm">
          No branch transfer documents found.
        </div>
      ) : (
        <div className="overflow-auto max-h-[460px]">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Transfer No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Dept No</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Branch Description</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Comments</TableCell>
                <TableCell sx={{ fontWeight: 700 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {docList.map((row) => {
                const isFetching = loadingItemsFor === row.TROUT_NO;
                return (
                  <TableRow
                    key={row.TROUT_NO}
                    hover
                    onClick={() => !loadingItemsFor && handleRowClick(row)}
                    sx={{
                      cursor: loadingItemsFor ? "wait" : "pointer",
                      opacity: loadingItemsFor && !isFetching ? 0.5 : 1,
                    }}
                  >
                    <TableCell>
                      <span className="font-semibold text-blue-600">
                        {row.TROUT_NO}
                      </span>
                    </TableCell>
                    <TableCell>{row.DATE?.split("T")[0]}</TableCell>
                    <TableCell>{row.DEPTNO?.trim()}</TableCell>
                    <TableCell>{row["BRANCH DESCRIPTION"]}</TableCell>
                    <TableCell>{row.COMMENTS || "-"}</TableCell>
                    <TableCell>
                      {isFetching && <CircularProgress size={16} />}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button onClick={onClose} variant="outlined" size="small" disabled={!!loadingItemsFor}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default BranchTransferDocListModal;